import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { NgxResizeObserverService } from 'ngx-resize-observer';
import { Subject, distinctUntilChanged, takeUntil, throttleTime } from 'rxjs';

const PRIMARY_SELECTOR = '.primary';
const SECONDARY_SELECTOR = '.secondary';

@Component({
  selector: 'shrd-pf-ui-content-slider',
  templateUrl: './header-content-slider.component.html',
  styleUrls: ['./header-content-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderContentSliderComponent implements AfterViewInit, OnChanges, OnDestroy {
  @HostBinding('class.slide-on')
  @Input()
  slideOn = false;

  private readonly element: HTMLElement;
  private readonly resizeSubject = new Subject<number | string>();
  private readonly unsubscribe$ = new Subject<void>();

  @ViewChild('sliderContent') private readonly sliderContent: ElementRef<HTMLElement>;

  constructor(
    { nativeElement }: ElementRef,
    private readonly renderer: Renderer2,
    private readonly resizeObserver: NgxResizeObserverService
  ) {
    this.element = nativeElement;
  }

  ngAfterViewInit(): void {
    this.observeSizeChange();
  }

  ngOnChanges({ slideOn }: SimpleChanges): void {
    if (!slideOn.firstChange) {
      this.updateHeight();
    }
  }

  ngOnDestroy(): void {
    this.unobserveSizeChange();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private updateHeight(): void {
    const selector = this.slideOn ? SECONDARY_SELECTOR : PRIMARY_SELECTOR;
    const newHeight = this.element.querySelector(selector)?.scrollHeight || this.element.clientHeight;
    if (newHeight) {
      this.renderer.setStyle(this.element, 'height', `${newHeight}px`);
    }
  }

  private observeSizeChange(): void {
    // observe content height change
    this.resizeObserver.observe(
      this.sliderContent.nativeElement,
      ({ contentRect: { height } }) => {
        this.resizeSubject.next(height.toFixed());
      },
      'border-box'
    );

    this.resizeSubject
      .pipe(distinctUntilChanged(), throttleTime(100), takeUntil(this.unsubscribe$))
      .subscribe(_ => this.updateHeight());
  }

  private unobserveSizeChange(): void {
    this.resizeObserver.unobserve(this.sliderContent.nativeElement);
  }
}
